/**
 * @fileoverview Single date picker component with calendar popover.
 * @module shared/components/ui/DatePicker
 */

import { ReactElement, useState } from 'react'
import { CalendarDaysIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/shared/components/ui'
import { Calendar } from '@/components/ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { formatDateHuman } from '@/shared/lib/utils'
import { pl, enUS } from 'date-fns/locale'
import { cn } from '@/lib/utils'

export interface DatePickerProps {
  /** Selected date value */
  value?: Date | null
  /** Callback when date changes */
  onChange: (date: Date | null) => void
  /** Placeholder text when no date selected */
  placeholder?: string
  /** Additional className for trigger button */
  className?: string
  /** Disable the picker */
  disabled?: boolean
  /** Align popover content relative to trigger */
  align?: 'start' | 'center' | 'end'
}

/**
 * Single date picker with calendar popover.
 */
export function DatePicker({
  value,
  onChange,
  placeholder = 'Select date',
  className,
  disabled = false,
  align = 'start',
}: DatePickerProps): ReactElement {
  const { i18n } = useTranslation('common')
  const [isOpen, setIsOpen] = useState(false)

  const locale = i18n.language === 'pl' ? pl : enUS

  const handleSelect = (date: Date | undefined): void => {
    onChange(date ?? null)
    setIsOpen(false)
  }

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          disabled={disabled}
          className={cn(
            'w-full px-3 py-2 h-9 flex items-center justify-start text-left font-normal',
            !value && 'text-muted-foreground',
            className
          )}
        >
          <CalendarDaysIcon className="w-4 h-4 mr-2 text-muted-foreground" />
          <span>{value ? formatDateHuman(value, i18n.language) : placeholder}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align={align}>
        <Calendar
          mode="single"
          selected={value ?? undefined}
          onSelect={handleSelect}
          locale={locale}
          {...(value && { defaultMonth: value })}
          className="rounded-lg border-0"
        />
      </PopoverContent>
    </Popover>
  )
}

export default DatePicker
